import React, { useState, useCallback } from 'react';
import { useAllowedEmails } from '../../agents/adminAgent';
import './AllowedEmailsManager.css';

function AvatarInitial({ firstName, lastName, email }) {
  const initial = firstName
    ? firstName.charAt(0).toUpperCase()
    : email
    ? email.charAt(0).toUpperCase()
    : '?';
  return <span className="admin-avatar" aria-hidden="true">{initial}</span>;
}

function StatusBadge({ allowed }) {
  return (
    <span className={`admin-badge ${allowed ? 'admin-badge--allowed' : 'admin-badge--none'}`}>
      {allowed ? 'Allowed' : 'Not allowed'}
    </span>
  );
}

export default function AllowedEmailsManager() {
  const { entries, unregisteredUsers, loading, error, isAdmin, addEmail, removeEmail } =
    useAllowedEmails();

  const [newEmail, setNewEmail] = useState('');
  const [actionError, setActionError] = useState(null);
  const [busy, setBusy] = useState(false);

  const handleAdd = useCallback(
    async (email) => {
      const trimmed = email.trim();
      if (!trimmed) return;
      setBusy(true);
      setActionError(null);
      try {
        await addEmail(trimmed);
        setNewEmail('');
      } catch (err) {
        setActionError(err.message || 'Failed to add email');
      } finally {
        setBusy(false);
      }
    },
    [addEmail]
  );

  const handleRemove = useCallback(
    async (email) => {
      setBusy(true);
      setActionError(null);
      try {
        await removeEmail(email);
      } catch (err) {
        setActionError(err.message || 'Failed to remove email');
      } finally {
        setBusy(false);
      }
    },
    [removeEmail]
  );

  const handleInputKey = (e) => {
    if (e.key === 'Enter') handleAdd(newEmail);
  };

  if (loading) {
    return (
      <div className="admin-shell">
        <div className="admin-loading">Loading access control list...</div>
      </div>
    );
  }

  if (isAdmin === false) {
    return (
      <div className="admin-shell">
        <div className="admin-forbidden">
          <h2 className="admin-forbidden-title">Access Denied</h2>
          <p>You do not have permission to manage access control.</p>
        </div>
      </div>
    );
  }

  // Build a combined view of all registered users with their allowed status.
  // entries where is_registered=true are allowed registered users.
  const allowedRegistered = entries.filter((e) => e.is_registered);
  // unregisteredUsers are registered but not on the list.
  const allRegistered = [
    ...allowedRegistered.map((e) => ({
      id: e.user_id,
      first_name: e.first_name,
      last_name: e.last_name,
      email: e.email,
      role: e.role,
      allowed: true,
    })),
    ...unregisteredUsers.map((u) => ({ ...u, allowed: false })),
  ].sort((a, b) => {
    const nameA = `${a.first_name || ''} ${a.last_name || ''}`.trim().toLowerCase();
    const nameB = `${b.first_name || ''} ${b.last_name || ''}`.trim().toLowerCase();
    return nameA.localeCompare(nameB);
  });

  // Manual entries: allowed emails that are NOT registered.
  const manualEntries = entries.filter((e) => !e.is_registered);
  const listEmpty = entries.length === 0;

  return (
    <div className="admin-shell">
      {/* Header */}
      <div className="admin-header">
        <h1 className="admin-title">Access Control</h1>
        <p className="admin-subtitle">Manage who can register and access the application</p>
      </div>

      {listEmpty && (
        <div className="admin-banner admin-banner--info">
          The allowlist is empty — registration is open to everyone.
        </div>
      )}

      {(error || actionError) && (
        <div className="admin-banner admin-banner--error">
          {actionError || error}
        </div>
      )}

      {/* Registered Users Section */}
      <section className="admin-section">
        <h2 className="admin-section-title">Registered Users</h2>
        <p className="admin-section-desc">
          Toggle access for users who already have an account.
        </p>

        {allRegistered.length === 0 ? (
          <p className="admin-empty">No registered users yet.</p>
        ) : (
          <div className="admin-user-list">
            {allRegistered.map((u) => (
              <div key={u.email} className="admin-user-row">
                <AvatarInitial firstName={u.first_name} lastName={u.last_name} email={u.email} />
                <div className="admin-user-info">
                  <span className="admin-user-name">
                    {u.first_name || ''} {u.last_name || ''}
                  </span>
                  <span className="admin-user-email">{u.email}</span>
                  {u.role && <span className="admin-user-role">{u.role}</span>}
                </div>
                <div className="admin-user-actions">
                  <StatusBadge allowed={u.allowed} />
                  {u.allowed ? (
                    <button
                      className="admin-btn admin-btn--remove"
                      onClick={() => handleRemove(u.email)}
                      disabled={busy}
                    >
                      Remove
                    </button>
                  ) : (
                    <button
                      className="admin-btn admin-btn--add"
                      onClick={() => handleAdd(u.email)}
                      disabled={busy}
                    >
                      Allow
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Manual Email Entry Section */}
      <section className="admin-section">
        <h2 className="admin-section-title">Manual Email Entry</h2>
        <p className="admin-section-desc">
          Add emails for people who have not yet registered.
        </p>

        <div className="admin-add-row">
          <input
            className="admin-input"
            type="email"
            placeholder="email@example.com"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            onKeyDown={handleInputKey}
            disabled={busy}
            aria-label="Email address to add"
          />
          <button
            className="admin-btn admin-btn--add"
            onClick={() => handleAdd(newEmail)}
            disabled={busy || !newEmail.trim()}
          >
            Add
          </button>
        </div>

        {manualEntries.length > 0 && (
          <ul className="admin-manual-list" aria-label="Manually added emails">
            {manualEntries.map((e) => (
              <li key={e.email} className="admin-manual-item">
                <span className="admin-manual-email">{e.email}</span>
                <button
                  className="admin-btn-icon"
                  onClick={() => handleRemove(e.email)}
                  disabled={busy}
                  aria-label={`Remove ${e.email}`}
                  title="Remove"
                >
                  &times;
                </button>
              </li>
            ))}
          </ul>
        )}

        {manualEntries.length === 0 && (
          <p className="admin-empty">No manually added emails.</p>
        )}
      </section>
    </div>
  );
}
